import React, { useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { CREATIVE_IMAGE } from '../../../../utils/reportsConsts';
import CreativePreview from '../../../../components/creatives/CreativePreview';
import KpiHeaderComponent from './KpiHeaderComponent';
import css from './InsightAgGridTable.css';
import { AUTO_GROUP_COLUMN_DEF, CREATIVE_PREVIEW, CREATIVE_TYPE, DEFAULT_COL_DEF, URL_KEY } from '../consts';
import { useLocalStorageState } from '../../../../utils/customHooks';

const convertKPIValueToNumber = val => {
    if (val === null || val === undefined || val === '') return NaN;
    if (typeof val === 'number') return val;
    const cleanedVal = String(val).replace(/[^\d.-]/g, '');
    return cleanedVal === '' ? NaN : Number(cleanedVal);
};

const CreativePreviewCellRenderer = ({ value }) => {
    if (!value) return null;
    return <CreativePreview url={value} className={css.creativePreview} />;
};

const hasValidTableData = table => {
    return table && table.length > 1;
};

const InsightAgGridTable = ({ table, showRowGroupPanel = false, dimensionColumns = [], metricAggregations = {} }) => {
    const [selectedKpi, setSelectedKpi] = useState('Total Cost');
    const [savedRowGroupState, setSavedRowGroupState] = useLocalStorageState('insights-top-bottom-row-groups', null);
    const gridRef = useRef(null);
    const columnOrderRef = useRef(null);

    const kpiColumns = useMemo(() => {
        if (!hasValidTableData(table)) return [];
        const rawHeaders = table[0];
        return rawHeaders
            .filter(header => typeof header === 'object' && header.isKpi === true)
            .map(header => header.header);
    }, [table]);

    const saveColumnState = () => {
        if (gridRef.current?.api) {
            const columnState = gridRef.current.api.getColumnState();
            columnOrderRef.current = columnState;

            if (showRowGroupPanel) {
                const rowGroupInfo = columnState
                    .filter(column => column.rowGroup)
                    .map(column => ({ colId: column.colId, rowGroupIndex: column.rowGroupIndex }));
                setSavedRowGroupState(rowGroupInfo);
            }
        }
    };

    const handleKpiChange = newKpi => {
        saveColumnState();
        setSelectedKpi(newKpi);
    };

    const { columnDefs, rowData, hasImageInTable } = useMemo(() => {
        if (!hasValidTableData(table)) {
            return { columnDefs: [], rowData: [] };
        }

        const rawHeaders = table[0];
        const headers = rawHeaders.map(header => {
            return typeof header === 'object' && header.header ? header.header : header;
        });
        // Extract data rows (skip the first row which contains headers)
        const rows = table.slice(1);

        let kpiColumnCreated = false;

        const allColDefs = [];
        let hasUrlColumn = false;
        headers.forEach(header => {
            const isUrlColumn = header?.toLowerCase() === URL_KEY;
            const isKpiColumn = kpiColumns.includes(header);

            if (isUrlColumn) {
                hasUrlColumn = true;
            }

            if (isKpiColumn && kpiColumnCreated) return;
            if (isKpiColumn) kpiColumnCreated = true;

            const fieldName = isUrlColumn ? CREATIVE_IMAGE : isKpiColumn ? selectedKpi : header;

            const colDef = {
                field: fieldName,
                colId: fieldName,
                headerName: isUrlColumn ? CREATIVE_PREVIEW : String(fieldName || ''),
                sortable: true,
                resizable: true,
                minWidth: isUrlColumn ? 120 : 100,
                flex: isUrlColumn ? 0 : 1,
                headerTooltip: isUrlColumn ? CREATIVE_PREVIEW : String(fieldName || ''),
            };

            if (showRowGroupPanel) {
                const isDimensionColumn = dimensionColumns.includes(header);
                const isMetricColumn = Object.keys(metricAggregations).includes(header);
                if (isDimensionColumn) {
                    colDef.enableRowGroup = true;
                    colDef.enableValue = false;
                    if (savedRowGroupState) {
                        const savedState = savedRowGroupState.find(state => state.colId === colDef.colId);
                        if (savedState) {
                            colDef.rowGroup = true;
                            colDef.rowGroupIndex = savedState.rowGroupIndex;
                        }
                    }
                } else if (isMetricColumn) {
                    colDef.enableRowGroup = false;
                    colDef.enableValue = true;
                    colDef.aggFunc = metricAggregations[header];
                }
            }

            if (isUrlColumn) {
                colDef.cellRenderer = CreativePreviewCellRenderer;
                colDef.sortable = false;
                colDef.suppressSizeToFit = true;
                colDef.tooltipValueGetter = params => {
                    return `Click to watch the ${params.data?.[CREATIVE_TYPE] || 'Creative'}`;
                };
            } else {
                colDef.comparator = (a, b) => {
                    const numA = convertKPIValueToNumber(a);
                    const numB = convertKPIValueToNumber(b);

                    const aNaN = Number.isNaN(numA);
                    const bNaN = Number.isNaN(numB);

                    if (aNaN && bNaN) return String(a || '').localeCompare(String(b || ''));
                    if (aNaN) return 1;
                    if (bNaN) return -1;

                    return numA - numB;
                };
            }

            if (isKpiColumn) {
                // Use stable colId so AG-Grid can track this column across kpis updates when the column order changes
                colDef.colId = 'kpi-metric';
                colDef.sortable = true;
                colDef.suppressMenu = true;
                colDef.headerComponent = KpiHeaderComponent;
                colDef.headerComponentParams = {
                    onKpiChange: handleKpiChange,
                    selectedKpi,
                    kpiColumns,
                };
                colDef.headerTooltip = selectedKpi;
            }

            allColDefs.push(colDef);
        });

        // Reorder columns based on saved state order
        let finalColDefs = allColDefs;
        if (columnOrderRef.current?.length > 0) {
            finalColDefs = columnOrderRef.current
                .map(colState => allColDefs.find(colDef => colDef.colId === colState.colId))
                .filter(Boolean);
        }

        const data = rows.map((row, rowIndex) => {
            const rowObj = { id: `row_${rowIndex}` };
            headers.forEach((header, colIndex) => {
                const isUrlColumn = header?.toLowerCase() === URL_KEY;

                if (isUrlColumn) {
                    rowObj[CREATIVE_IMAGE] = row[colIndex];
                    hasUrlColumn = true;
                } else {
                    rowObj[header] = row[colIndex];
                }
            });
            return rowObj;
        });

        return { columnDefs: finalColDefs, rowData: data, hasImageInTable: hasUrlColumn };
    }, [table, selectedKpi, savedRowGroupState, kpiColumns, dimensionColumns, metricAggregations, showRowGroupPanel]);

    if (!rowData.length) {
        return null;
    }

    return (
        <div className={css.gridContainer}>
            <AgGridReact
                ref={gridRef}
                className="ag-theme-quartz"
                modules={[AllEnterpriseModule]}
                columnDefs={columnDefs}
                rowData={rowData}
                defaultColDef={DEFAULT_COL_DEF}
                autoGroupColumnDef={showRowGroupPanel ? AUTO_GROUP_COLUMN_DEF : undefined}
                domLayout="autoHeight"
                rowHeight={hasImageInTable ? 70 : 50}
                headerHeight={40}
                suppressContextMenu
                suppressDragLeaveHidesColumns
                suppressAggFuncInHeader
                getRowId={params => params.data.id}
                rowGroupPanelShow={showRowGroupPanel ? 'always' : 'never'}
                groupDefaultExpanded={showRowGroupPanel ? 0 : undefined}
                tooltipShowDelay={500}
                suppressColumnMoveAnimation
                onColumnMoved={saveColumnState}
                onColumnRowGroupChanged={saveColumnState}
            />
        </div>
    );
};

InsightAgGridTable.propTypes = {
    table: PropTypes.array.isRequired,
    showRowGroupPanel: PropTypes.bool,
    dimensionColumns: PropTypes.arrayOf(PropTypes.string),
    metricAggregations: PropTypes.objectOf(PropTypes.string),
};

CreativePreviewCellRenderer.propTypes = {
    value: PropTypes.string,
};

export default InsightAgGridTable;
